/**
 * useAddSharedBasketToCart — recipient-side write hook.
 * Separated from useViewSharedBasket to keep read and write concerns distinct.
 * Receives an already-enriched basket and handles the CT cart mutation.
 */
import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { appConfig } from '../../../config/app-config';
import { addItemsToCart } from '../../cart/api/cartApi';
import { NAVIGATION_DELAY_MS, SHARE_ERROR_MESSAGES, SHARE_SUCCESS_MESSAGES } from '../constants/messages';
import { EnrichedBasket } from '../types/sharedBasket.types';

interface CartLineItemDraft {
  productId: string;
  variantId: number;
  quantity: number;
  externalPrice: { centAmount: number; currencyCode: string };
  custom: {
    type: { typeId: string; id: string };
    fields: Record<string, unknown>;
  };
}

interface UseAddSharedBasketToCartOptions {
  sharedBasket: EnrichedBasket | null;
  showError: (message: string) => void;
  showSuccess: (title: string, description?: string) => void;
}

export function useAddSharedBasketToCart({
  sharedBasket,
  showError,
  showSuccess,
}: UseAddSharedBasketToCartOptions) {
  const navigate = useNavigate();
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addingToCart, setAddingToCart] = useState(false);

  const handleAddToCart = useCallback(async () => {
    if (!sharedBasket) return;

    const typeId = import.meta.env.VITE_LINE_ITEM_TYPE_ID as string | undefined;
    if (!typeId) {
      showError(SHARE_ERROR_MESSAGES.CONFIG_ERROR);
      return;
    }

    setAddingToCart(true);

    try {
      const itemsToBatch: CartLineItemDraft[] = [];

      sharedBasket.lineItems.forEach((snapshotItem) => {
        const product = sharedBasket.productMap[snapshotItem.productId];
        if (!product) return;

        const variant = product.masterData.current.masterVariant;
        const delegateRate = snapshotItem.customFields?.delegateRate;
        const priceObj =
          (delegateRate && sharedBasket.customerGroupMap[delegateRate]
            ? variant.prices?.find(
                (p) => p.customerGroup?.id === sharedBasket.customerGroupMap[delegateRate].id,
              )
            : undefined) ?? variant.prices?.[0];

        const currentPrice =
          priceObj?.discounted?.value?.centAmount ?? priceObj?.value?.centAmount ?? 0;
        const currencyCode = priceObj?.value?.currencyCode ?? sharedBasket.currency ?? 'USD';

        itemsToBatch.push({
          productId: product.id,
          variantId: variant.id,
          quantity: snapshotItem.quantity,
          externalPrice: { centAmount: currentPrice, currencyCode },
          custom: {
            type: { typeId: 'type', id: typeId },
            fields: snapshotItem.customFields ?? {},
          },
        });
      });

      if (itemsToBatch.length === 0) {
        showError(SHARE_ERROR_MESSAGES.NO_VALID_ITEMS);
        return;
      }

      await addItemsToCart(
        itemsToBatch.map((item) => ({
          ...item,
          externalPrice: item.externalPrice.centAmount,
          currency: item.externalPrice.currencyCode,
        }))
      );
      showSuccess(SHARE_SUCCESS_MESSAGES.ITEMS_ADDED, SHARE_SUCCESS_MESSAGES.ITEMS_ADDED_DESC);

      const urlParams = new URLSearchParams(window.location.search);
      const eventKey = urlParams.get('event') ?? appConfig.defaults.eventKey;

      if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => navigate(`/${eventKey}/cart`), NAVIGATION_DELAY_MS);
    } catch (err) {
      console.error('[useAddSharedBasketToCart] Failed to add items to cart:', err);
      const message = err instanceof Error ? err.message : '';
      if (message.includes('network')) {
        showError(SHARE_ERROR_MESSAGES.NETWORK_ERROR);
      } else {
        showError(SHARE_ERROR_MESSAGES.ADD_TO_CART_FAILED);
      }
    } finally {
      setAddingToCart(false);
    }
  }, [sharedBasket, showError, showSuccess, navigate]);

  return { addingToCart, handleAddToCart };
}
